import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { supabase } from '../webSupabaseClient';
import { toast } from 'react-toastify';
import { TrashIcon, PlusIcon, PhotoIcon } from '@heroicons/react/24/outline';

const initialSettings = {
    featureItems: [
        { icon: 'ShieldCheckIcon', text: 'Güvenli Alışveriş' },
        { icon: 'TruckIcon', text: '1000 TL Üzeri Ücretsiz Kargo' },
        { icon: 'ArrowPathRoundedSquareIcon', text: 'Ücretsiz İade' },
        { icon: 'BuildingStorefrontIcon', text: 'Online Değişim' },
        { icon: 'CreditCardIcon', text: 'Vade Farksız 4 Taksit' },
    ],
    footerLinks: { 
        "Kategoriler": [], 
        "Popüler Markalar": [], 
        "Müşteri Hizmetleri": [] 
    },
    contactInfo: { 
        address: '', 
        phone: '', 
        email: '', 
        instagram: 'https://www.instagram.com/minelakids_2025?utm_source=qr&igsh=dnY3M2JnNWRzNGN3' 
    },
    heroBanners: [],
    kargonomiSettings: {
        enabled: false,
        apiToken: '',
        testMode: true
    }
};

function SettingsManagementPanel() {
    const { token } = useAuth();
    const [settings, setSettings] = useState(initialSettings);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api('/api/settings');
            console.log('📊 API\'den gelen ayarlar:', data); // Debug için
            console.log('🖼️ Gelen bannerlar:', data.heroBanners); // Debug için
            setSettings({
                featureItems: (data.featureItems && data.featureItems.length === 5) ? data.featureItems : initialSettings.featureItems,
                footerLinks: data.footerLinks || initialSettings.footerLinks,
                contactInfo: data.contactInfo || initialSettings.contactInfo,
                heroBanners: data.heroBanners || initialSettings.heroBanners,
            });
        } catch (err) {
            console.error('❌ Ayarlar yüklenirken hata:', err);
            toast.error("Ayarlar yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await api('/api/settings', {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(settings)
            });
            toast.success("Ayarlar başarıyla kaydedildi!");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleBannerChange = async (index, field, value) => {
        const newBanners = [...settings.heroBanners];
        newBanners[index][field] = value;
        const updatedSettings = { ...settings, heroBanners: newBanners };
        setSettings(updatedSettings);
        
        // Link değişikliklerini otomatik kaydet (sadece linkUrl için)
        if (field === 'linkUrl') {
            try {
                await api('/api/settings', {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(updatedSettings)
                });
                // Sessiz kaydetme - toast gösterme
            } catch(err) {
                console.error('Banner link kaydedilemedi:', err);
            }
        }
    };

    const handleBannerImageUpload = async (index, file) => {
        if (!file) return;
        setIsSaving(true);
        const fileName = `banner-${Date.now()}-${file.name}`;
        const filePath = `public-assets/banners/${fileName}`;

        try {
            const { error: uploadError } = await supabase.storage
                .from('product-images')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
                .from('product-images')
                .getPublicUrl(filePath);
            
            // Banner URL'sini güncelle
            const newBanners = [...settings.heroBanners];
            newBanners[index] = { ...newBanners[index], imageUrl: urlData.publicUrl };
            
            const updatedSettings = { ...settings, heroBanners: newBanners };
            setSettings(updatedSettings);
            
            // Otomatik olarak veritabanına kaydet
            await api('/api/settings', {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(updatedSettings)
            });
            
            toast.success("Banner görseli yüklendi ve kaydedildi!");
        } catch(err) {
            toast.error(`Görsel yüklenemedi: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const addBanner = async () => {
        const updatedSettings = {
            ...settings,
            heroBanners: [...(settings.heroBanners || []), { imageUrl: '', linkUrl: '' }]
        };
        setSettings(updatedSettings);
        
        // Otomatik kaydet
        try {
            setIsSaving(true);
            await api('/api/settings', {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(updatedSettings)
            });
            toast.success("Yeni banner alanı eklendi!");
        } catch(err) {
            toast.error(`Banner eklenemedi: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const removeBanner = async (index) => {
        const newBanners = settings.heroBanners.filter((_, i) => i !== index);
        const updatedSettings = { ...settings, heroBanners: newBanners };
        setSettings(updatedSettings);
        
        // Otomatik kaydet
        try {
            setIsSaving(true);
            await api('/api/settings', {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(updatedSettings)
            });
            toast.success("Banner silindi!");
        } catch(err) {
            toast.error(`Banner silinemedi: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleFeatureChange = (index, value) => {
        const newItems = [...settings.featureItems];
        newItems[index] = { ...newItems[index], text: value };
        setSettings(prev => ({ ...prev, featureItems: newItems }));
    };

    const handleLinkChange = (category, index, field, value) => {
        const newLinks = [...(settings.footerLinks[category] || [])];
        newLinks[index][field] = value;
        setSettings(prev => ({
            ...prev,
            footerLinks: { ...prev.footerLinks, [category]: newLinks }
        }));
    };
    
    const addLink = (category) => {
        const newLinks = [...(settings.footerLinks[category] || []), { name: '', path: '' }];
        setSettings(prev => ({...prev, footerLinks: {...prev.footerLinks, [category]: newLinks}}));
    };

    const removeLink = (category, index) => {
        const newLinks = settings.footerLinks[category].filter((_, i) => i !== index);
        setSettings(prev => ({...prev, footerLinks: {...prev.footerLinks, [category]: newLinks}}));
    };

    const handleContactChange = (e) => {
        const {name, value} = e.target;
        setSettings(prev => ({...prev, contactInfo: {...prev.contactInfo, [name]: value}}));
    };

    if (loading) return <div>Ayarlar yükleniyor...</div>;

    return (
        <div className="container mx-auto p-2 sm:p-4">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-4 sm:p-6 text-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Site Ayarları</h2>
                    <p className="opacity-90 text-sm sm:text-base">Web sitesi ayarlarını yönetin</p>
                </div>
                
                <div className="p-4 sm:p-6">
                    <div className="space-y-8">
            <div>
                <h3 className="text-xl font-bold mb-4">Ana Sayfa Banner Yönetimi</h3>
                <div className="p-4 border rounded-md bg-gray-50 space-y-4">
                    {(settings.heroBanners || []).map((banner, index) => (
                        <div key={index} className="flex flex-col md:flex-row items-center gap-4 p-3 border-b">
                           <div className="flex-shrink-0 w-32 h-16 bg-gray-200 rounded flex items-center justify-center">
                               {banner.imageUrl ? <img src={banner.imageUrl} alt={`Banner ${index+1}`} className="w-full h-full object-cover rounded"/> : <PhotoIcon className="w-8 h-8 text-gray-400"/>}
                           </div>
                           <div className="flex-grow w-full space-y-2">
                                <input type="text" value={banner.linkUrl} onChange={e => handleBannerChange(index, 'linkUrl', e.target.value)} placeholder="Banner Linki (örn: /products?category=Elbise)" className="w-full p-2 border rounded-md text-sm" />
                                <input type="file" onChange={e => handleBannerImageUpload(index, e.target.files[0])} className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                           </div>
                            <button onClick={() => removeBanner(index)} className="p-2 text-red-500 hover:bg-red-100 rounded-full"><TrashIcon className="w-5 h-5" /></button>
                        </div>
                    ))}
                    <button onClick={addBanner} className="mt-2 text-sm text-blue-600 flex items-center gap-1"><PlusIcon className="w-4 h-4"/> Yeni Banner Ekle</button>
                </div>
            </div>

            <div>
                <h3 className="text-xl font-bold mb-4">Footer Özellik Bandı</h3>
                <div className="space-y-3 p-4 border rounded-md bg-gray-50">
                    {(settings.featureItems || []).map((item, index) => (
                        <div key={index}>
                            <label className="text-sm font-medium text-gray-600">Özellik {index + 1}</label>
                            <input type="text" value={item.text} onChange={(e) => handleFeatureChange(index, e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md mt-1" />
                        </div>
                    ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Not: İkonlar sabittir, sadece metinleri değiştirebilirsiniz.</p>
            </div>

            <div>
                <h3 className="text-xl font-bold mb-4">Footer Linkleri</h3>
                {Object.entries(settings.footerLinks || {}).map(([category, links]) => (
                    <div key={category} className="mb-6 p-4 border rounded-md bg-gray-50">
                        <h4 className="font-semibold mb-2">{category}</h4>
                        <div className="space-y-2">
                        {(links || []).map((link, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <input type="text" placeholder="Görünecek İsim" value={link.name} onChange={(e) => handleLinkChange(category, index, 'name', e.target.value)} className="flex-1 p-2 border rounded-md text-sm" />
                                <input type="text" placeholder="Gidilecek Yol (örn: /hakkimizda)" value={link.path} onChange={(e) => handleLinkChange(category, index, 'path', e.target.value)} className="flex-1 p-2 border rounded-md text-sm" />
                                <button onClick={() => removeLink(category, index)} className="p-2 text-red-500 hover:bg-red-100 rounded-full"><TrashIcon className="w-5 h-5" /></button>
                            </div>
                        ))}
                        </div>
                        <button onClick={() => addLink(category)} className="mt-2 text-sm text-blue-600 flex items-center gap-1"><PlusIcon className="w-4 h-4"/> Yeni Link Ekle</button>
                    </div>
                ))}
            </div>

            <div>
                <h3 className="text-xl font-bold mb-4">Footer İletişim Bilgileri</h3>
                <div className="space-y-4 p-4 border rounded-md bg-gray-50">
                    <div><label className="text-sm font-medium text-gray-600">Adres</label><input name="address" value={settings.contactInfo.address} onChange={handleContactChange} className="w-full p-2 border border-gray-300 rounded-md mt-1" /></div>
                    <div><label className="text-sm font-medium text-gray-600">Telefon</label><input name="phone" value={settings.contactInfo.phone} onChange={handleContactChange} className="w-full p-2 border border-gray-300 rounded-md mt-1" /></div>
                    <div><label className="text-sm font-medium text-gray-600">E-posta</label><input name="email" value={settings.contactInfo.email} onChange={handleContactChange} className="w-full p-2 border border-gray-300 rounded-md mt-1" /></div>
                    <div><label className="text-sm font-medium text-gray-600">Instagram Linki</label><input name="instagram" value={settings.contactInfo.instagram} onChange={handleContactChange} className="w-full p-2 border border-gray-300 rounded-md mt-1" /></div>
                </div>
            </div>

            <div className="text-right pt-4">
                <button onClick={handleSave} disabled={isSaving} className="bg-brand-green text-white font-bold px-8 py-3 rounded-lg shadow-lg hover:bg-brand-green-light disabled:opacity-50">
                    {isSaving ? "Kaydediliyor..." : "Tüm Ayarları Kaydet"}
                </button>
            </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
// Kargonomi Ayarları Sekmesi
const KargonomiTab = ({ settings, setSettings }) => {
    const kargonomiSettings = settings.kargonomiSettings || {
        enabled: false,
        apiToken: '',
        testMode: true
    };

    const updateKargonomiSettings = (field, value) => {
        setSettings(prev => ({
            ...prev,
            kargonomiSettings: {
                ...prev.kargonomiSettings,
                [field]: value
            }
        }));
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kargonomi API Ayarları</h3>
                
                {/* API Aktif/Pasif */}
                <div className="mb-6">
                    <label className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            checked={kargonomiSettings.enabled}
                            onChange={(e) => updateKargonomiSettings('enabled', e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">
                            Kargonomi API'yi Aktif Et
                        </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                        Kapalı olduğunda mock sistem kullanılır
                    </p>
                </div>

                {/* API Token */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Token
                    </label>
                    <input
                        type="password"
                        value={kargonomiSettings.apiToken}
                        onChange={(e) => updateKargonomiSettings('apiToken', e.target.value)}
                        placeholder="Kargonomi API Token'ınızı girin"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                {/* Test Modu */}
                <div className="mb-6">
                    <label className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            checked={kargonomiSettings.testMode}
                            onChange={(e) => updateKargonomiSettings('testMode', e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">
                            Test Modu
                        </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                        Test modunda gerçek kargo oluşturulmaz
                    </p>
                </div>

                {/* Durum Göstergesi */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Mevcut Durum</h4>
                    <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                            kargonomiSettings.enabled && kargonomiSettings.apiToken 
                                ? 'bg-green-500' 
                                : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm text-gray-600">
                            {kargonomiSettings.enabled && kargonomiSettings.apiToken 
                                ? 'Kargonomi API Aktif' 
                                : 'Mock Sistem Aktif'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsManagementPanel;